import React, { useState, useEffect } from 'react';
import {
  FiAward,
  FiTrendingUp,
  FiDollarSign,
  FiPercent,
  FiCalendar,
  FiSettings,
  FiX,
  FiAlertCircle,
  FiActivity,
  FiClock,
  FiShare2,
  FiRefreshCw,
} from 'react-icons/fi';
import { useApi } from '../../hooks/useApi';
import { Loader, LoadingButton } from '../Common/Loader';
import { ErrorDisplay } from '../Common/ErrorDisplay';
import { CopyRelationship } from '../../types';
import { showToast } from '../../utils/toast';
import { socketService } from '../../services/socketService';
import ShareCopyModal from './ShareCopyModal';

interface ActiveCopiesProps {
  selectedBrokerId?: string | null;
}

const ActiveCopies: React.FC<ActiveCopiesProps> = ({ selectedBrokerId }) => {
  const { data: relationshipsData, isLoading, error, refetch } = useApi.useCopyRelationships(selectedBrokerId);

  // Listen for copy relationship updates via WebSocket
  useEffect(() => {
    const socket = socketService.getSocket();
    if (!socket) return;

    const handleCopyUpdate = () => {
      console.log('🔄 Copy relationship update received in ActiveCopies, refetching...');
      refetch();
    };

    socket.on('copy:update', handleCopyUpdate);

    return () => {
      socket.off('copy:update', handleCopyUpdate);
    };
  }, [refetch]);
  const [selectedRelationship, setSelectedRelationship] = useState<CopyRelationship | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [closePositions, setClosePositions] = useState(true);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editAllocation, setEditAllocation] = useState(10);
  const [editMaxInvestment, setEditMaxInvestment] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareRelationship, setShareRelationship] = useState<CopyRelationship | null>(null);

  const cancelMutation = useApi.useCancelCopyTrading({
    onSuccess: (data: { success: boolean; message?: string; closedPositions?: number }) => {
      const message = data.message || 'Copy trading cancelled successfully';
      showToast('success', message);
      setShowCancelModal(false);
      setSelectedRelationship(null);
      // Don't call refetch - mutation already invalidates queries
    },
    onError: (err: any) => {
      showToast('error', err.response?.data?.error || 'Failed to cancel copy trading');
    },
  });

  const pauseForDayMutation = useApi.usePauseCopyTradingForDay({
    onSuccess: (data) => {
      showToast('success', data.message || 'Copy trading paused for today');
    },
    onError: (err: any) => {
      showToast('error', err.response?.data?.error || 'Failed to pause copy trading');
    },
  });

  const updateMutation = useApi.useUpdateCopySettings({
    onSuccess: (data) => {
      showToast('success', data.message || 'Settings updated successfully');
      setShowEditModal(false);
      setSelectedRelationship(null);
      // Don't call refetch - mutation already invalidates queries
    },
    onError: (err: any) => {
      showToast('error', err.response?.data?.error || 'Failed to update settings');
    },
  });

  const handleCancelClick = (relationship: CopyRelationship) => {
    setSelectedRelationship(relationship);
    setShowCancelModal(true);
  };

  const handleEditClick = (relationship: CopyRelationship) => {
    setSelectedRelationship(relationship);
    const all = relationshipsData?.relationships || [];
    const others = all
      .filter(
        (r: CopyRelationship) =>
          r._id !== relationship._id &&
          ['active', 'paused', 'pending'].includes(r.status)
      )
      .reduce((sum: number, r: CopyRelationship) => sum + (Number(r.allocationPercent) || 0), 0);
    const maxAllowed = Math.max(0, 100 - others);
    setEditAllocation(Math.min(relationship.allocationPercent || 0, maxAllowed));
    setEditMaxInvestment(relationship.maxInvestmentAmount?.toString() || '');
    setShowEditModal(true);
  };

  const handleConfirmCancel = () => {
    if (!selectedRelationship) return;
    cancelMutation.mutate({
      relationshipId: selectedRelationship._id,
      closePositions,
    });
  };

  const handlePauseForDay = () => {
    const ok = window.confirm(
      'Pause copy trading for all masters until the end of the US trading day (ET)?\n\nNew trades will not be copied. Trading resumes automatically after today ends — no early resume.'
    );
    if (!ok) return;
    pauseForDayMutation.mutate();
  };

  const handleSaveEdit = () => {
    if (!selectedRelationship) return;
    const all = relationshipsData?.relationships || [];
    const others = all
      .filter(
        (r: CopyRelationship) =>
          r._id !== selectedRelationship._id &&
          ['active', 'paused', 'pending'].includes(r.status)
      )
      .reduce((sum: number, r: CopyRelationship) => sum + (Number(r.allocationPercent) || 0), 0);
    const remaining = Math.max(0, 100 - others);
    if (editAllocation > remaining) {
      showToast(
        'error',
        `Only ${remaining}% allocation remaining on this account (${others}% already used by other masters).`
      );
      return;
    }
    updateMutation.mutate({
      relationshipId: selectedRelationship._id,
      allocationPercent: editAllocation,
      maxInvestmentAmount: editMaxInvestment ? parseFloat(editMaxInvestment) : undefined,
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-6 animate-fade-in">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 shrink-0 bg-muted/20 rounded animate-pulse"></div>
            <div className="h-8 w-56 max-w-full bg-muted/20 rounded animate-pulse"></div>
          </div>
          <div className="text-right space-y-1">
            <div className="h-4 w-20 bg-muted/20 rounded animate-pulse ml-auto"></div>
            <div className="h-8 w-8 bg-muted/20 rounded animate-pulse ml-auto"></div>
          </div>
        </div>

        {/* Cards Skeleton */}
        <div className="grid grid-cols-1 gap-6">
          {[1, 2].map((i) => (
            <div key={i} className="card hover:shadow-xl transition-all duration-200">
              <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                  <div className="w-12 h-12 shrink-0 rounded-full bg-muted/20 animate-pulse"></div>
                  <div className="space-y-2 min-w-0">
                    <div className="h-6 w-40 max-w-full bg-muted/20 rounded animate-pulse"></div>
                    <div className="h-4 w-32 max-w-full bg-muted/20 rounded animate-pulse"></div>
                  </div>
                </div>
                <div className="flex gap-1 sm:gap-2 shrink-0">
                  {[1, 2, 3].map((j) => (
                    <div key={j} className="w-8 h-8 bg-muted/20 rounded animate-pulse"></div>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                {[1, 2, 3, 4].map((j) => (
                  <div key={j} className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                    <div className="h-4 w-16 bg-muted/20 rounded mb-2 animate-pulse"></div>
                    <div className="h-6 w-20 bg-muted/20 rounded animate-pulse"></div>
                  </div>
                ))}
              </div>
              <div className="bg-gradient-to-r from-primary-50 to-yellow-50 dark:from-gray-900 dark:to-gray-800 rounded-lg p-4">
                <div className="grid grid-cols-3 gap-2 sm:gap-4">
                  {[1, 2, 3].map((j) => (
                    <div key={j} className="text-center space-y-2 min-w-0">
                      <div className="h-4 w-20 max-w-full bg-muted/20 rounded mx-auto animate-pulse"></div>
                      <div className="h-8 w-12 bg-muted/20 rounded mx-auto animate-pulse"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return <ErrorDisplay error={error} />;
  }

  const relationships = relationshipsData?.relationships || [];
  const visibleRelationships = relationships.filter(
    (r: CopyRelationship) => r.status === 'active' || r.status === 'paused'
  );
  const activeRelationships = relationships.filter((r: CopyRelationship) => r.status === 'active');
  const selfPausedRelationships = relationships.filter(
    (r: CopyRelationship) =>
      r.status === 'paused' && String(r.pauseReason || '') === 'user_self_pause'
  );
  const isSelfPausedToday = selfPausedRelationships.length > 0;
  const selfPauseResumeAt =
    selfPausedRelationships.find((r) => r.copyTradingResumeAt)?.copyTradingResumeAt || null;

  // Other masters' allocation on this account (excludes the one being edited)
  const otherMastersUsed = selectedRelationship
    ? relationships
        .filter(
          (r: CopyRelationship) =>
            r._id !== selectedRelationship._id &&
            ['active', 'paused', 'pending'].includes(r.status)
        )
        .reduce((sum: number, r: CopyRelationship) => sum + (Number(r.allocationPercent) || 0), 0)
    : 0;
  const remainingForEdit = Math.max(0, 100 - otherMastersUsed);
  const editSliderMax = Math.max(0, remainingForEdit);
  const editWouldExceed = editAllocation > remainingForEdit;
  const totalAfterEdit = Math.min(100, otherMastersUsed + Math.min(editAllocation, remainingForEdit));
  const totalAccountUsed = visibleRelationships.reduce(
    (sum: number, r: CopyRelationship) => sum + (Number(r.allocationPercent) || 0),
    0
  );

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <FiActivity className="h-8 w-8 text-primary-600 shrink-0" />
          <h2 className="lg:text-3xl text-lg font-bold text-gray-900 dark:text-gray-100">Active Copy Trading</h2>
        </div>
        <div className="flex flex-wrap items-center justify-between sm:justify-end gap-3">
          {activeRelationships.length > 0 && !isSelfPausedToday && (
            <button
              type="button"
              onClick={handlePauseForDay}
              disabled={pauseForDayMutation.isPending}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400 text-sm font-semibold hover:bg-amber-500/15 disabled:opacity-50"
            >
              <FiClock className="h-4 w-4" />
              {pauseForDayMutation.isPending ? 'Pausing...' : 'Pause for today'}
            </button>
          )}
          <div className="text-right">
            <p className="text-sm text-gray-600 dark:text-gray-400">Total Active</p>
            <p className="text-2xl font-bold text-primary-600">{activeRelationships.length}</p>
            <p className={`text-xs font-semibold mt-0.5 ${totalAccountUsed > 100 ? 'text-red-600' : 'text-gray-500'}`}>
              {Math.min(totalAccountUsed, 999)}% allocated
            </p>
          </div>
        </div>
      </div>

      {isSelfPausedToday && (
        <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-3 flex items-start gap-3">
          <FiClock className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Copy trading paused for today</p>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
              All masters are paused. Trading resumes automatically
              {selfPauseResumeAt
                ? ` after ${new Date(selfPauseResumeAt).toLocaleString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    timeZoneName: 'short',
                  })}`
                : ' at the end of the US trading day (ET)'}
              .
            </p>
          </div>
        </div>
      )}

      {/* Copy Relationships */}
      {visibleRelationships.length === 0 ? (
        <div className="card text-center py-12">
          <FiActivity className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 lg:text-lg text-[14px] mb-2">No active copy relationships</p>
          <p className="text-gray-400 lg:text-lg text-[14px]">Start copying a master trader to see it here</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6">
          {visibleRelationships.map((relationship: CopyRelationship) => {
            const isPaused = relationship.status === 'paused';
            return (
              <div
                key={relationship._id}
                className={`card hover:shadow-xl transition-all duration-200 ${
                  isPaused ? 'opacity-90 border border-amber-500/30' : ''
                }`}
              >
                {/* Card Header */}
                <div className="flex items-start justify-between gap-2 sm:gap-3 mb-4">
                  <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                    <div className="p-2 sm:p-3 bg-primary-100 rounded-full shrink-0">
                      <FiAward className="h-6 w-6 text-primary-600" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 break-anywhere">
                          {relationship.master.name}
                        </h3>
                        {isPaused && (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400">
                            Paused
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 break-anywhere">{relationship.master.email}</p>
                      {isPaused && relationship.copyTradingResumeAt && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                          Resumes{' '}
                          {new Date(relationship.copyTradingResumeAt).toLocaleString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit',
                            timeZoneName: 'short',
                          })}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1 sm:gap-2 shrink-0">
                    <button
                      onClick={() => {
                        setShareRelationship(relationship);
                        setShowShareModal(true);
                      }}
                      className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                      title="Share"
                    >
                      <FiShare2 className="h-5 w-5" />
                    </button>
                    {!isPaused && (
                      <button
                        onClick={() => handleEditClick(relationship)}
                        className="p-2 text-primary-600 hover:bg-primary-100 rounded-lg transition-colors"
                        title="Edit Settings"
                      >
                        <FiSettings className="h-5 w-5" />
                      </button>
                    )}
                    <button
                      onClick={() => handleCancelClick(relationship)}
                      className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                      title="Cancel Copy Trading"
                    >
                      <FiX className="h-5 w-5" />
                    </button>
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 md:grid-cols-2 gap-2 sm:gap-4 mb-4">
                  <div className="bg-gray-50 rounded-lg p-3 min-w-0">
                    <div className="flex items-center gap-2 text-gray-600 mb-1">
                      <FiPercent className="h-4 w-4" />
                      <span className="text-xs font-medium">Allocation</span>
                    </div>
                    <p className="text-xl font-bold text-primary-600">
                      {relationship.allocationPercent}%
                    </p>
                  </div>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 min-w-0">
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300 mb-1">
                      <FiPercent className="h-4 w-4" />
                      <span className="text-xs font-medium">Investment</span>
                    </div>
                    <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                      {relationship.allocationPercent}%
                    </p>
                    <p className="text-[10px] text-gray-500 mt-0.5 break-anywhere">
                      of account · ${relationship.totalInvested.toLocaleString()} used
                    </p>
                  </div>
                </div>

                {/* Trade Stats Bar */}
                <div className="bg-gradient-to-r from-primary-50 to-yellow-50 dark:from-gray-900 dark:to-gray-800 rounded-lg p-3 sm:p-4 border-2 border-primary-200 dark:border-gray-700">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
                    <div className="text-center">
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Total Trades</p>
                      <p className="text-2xl font-bold text-black dark:text-white">
                        {relationship.totalCopiedTrades}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Success Rate</p>
                      <p className="text-2xl font-bold text-primary-600 dark:text-primary-400">
                        {relationship.successRate || 0}%
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Your P/L</p>
                      <p
                        className={`text-xl sm:text-2xl font-bold break-anywhere ${relationship.totalProfit >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}
                      >
                        ${relationship.totalProfit?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-sm text-gray-600 dark:text-gray-300 mb-1">Trader P/L</p>
                      <p
                        className={`text-xl sm:text-2xl font-bold break-anywhere ${(relationship as any).masterTotalProfit >= 0 ? 'text-green-600' : 'text-red-600'
                          }`}
                      >
                        ${(relationship as any).masterTotalProfit?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Additional Info */}
                <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap items-center justify-between gap-2 text-sm">
                  <div className="flex items-center gap-2 text-gray-600">
                    <FiCalendar className="h-4 w-4" />
                    <span>
                      Started: {new Date(relationship.startDate).toLocaleDateString()}
                    </span>
                  </div>
                  {relationship.maxInvestmentAmount && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <FiDollarSign className="h-4 w-4" />
                      <span>Max: ${relationship.maxInvestmentAmount.toLocaleString()}</span>
                    </div>
                  )}
                  {relationship.endDate && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <FiClock className="h-4 w-4" />
                      <span>Ends: {new Date(relationship.endDate).toLocaleDateString()}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && selectedRelationship && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <FiAlertCircle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">Cancel Copy Trading</h3>
            </div>

            <p className="text-gray-600 mb-4">
              Are you sure you want to stop copying <strong>{selectedRelationship.master.name}</strong>?
            </p>

            <label className="flex items-start gap-3 p-4 bg-yellow-50 border-2 border-yellow-200 rounded-lg mb-6">
              <input
                type="checkbox"
                checked={closePositions}
                onChange={(e) => setClosePositions(e.target.checked)}
                className="w-5 h-5 text-primary-600 mt-0.5"
              />
              <div>
                <p className="font-semibold text-black">Close all open positions</p>
                <p className="text-sm text-gray-600 mt-1">
                  This will automatically close all positions that were copied from this master
                  trader.
                </p>
              </div>
            </label>

            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3">
              <button
                onClick={() => {
                  setShowCancelModal(false);
                  setSelectedRelationship(null);
                }}
                className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 disabled:opacity-50"
                disabled={cancelMutation.isPending}
              >
                Keep Copying
              </button>
              <button
                onClick={handleConfirmCancel}
                className="flex-1 px-4 py-2 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                disabled={cancelMutation.isPending}
              >
                {cancelMutation.isPending ? (
                  <>
                    <FiRefreshCw className="h-4 w-4 animate-spin" />
                    Cancelling...
                  </>
                ) : (
                  'Cancel Copy Trading'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedRelationship && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-4 sm:p-6">
            <div className="flex items-center justify-between gap-3 mb-6">
              <div className="flex items-center gap-3 min-w-0">
                <div className="p-3 bg-primary-100 dark:bg-primary-900 rounded-full shrink-0">
                  <FiSettings className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100">Edit Copy Settings</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 break-anywhere">
                    {selectedRelationship.master.name}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowEditModal(false)}
                className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
              >
                <FiX className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Cumulative account allocation bar */}
              <div className="rounded-lg border-2 border-gray-200 dark:border-gray-700 p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                    Account allocation
                  </span>
                  <span className={`text-xs font-semibold ${totalAfterEdit > 100 || editWouldExceed ? 'text-red-600' : 'text-gray-500'}`}>
                    {totalAfterEdit}% / 100%
                  </span>
                </div>
                <div className="h-2.5 bg-gray-200 dark:bg-gray-800 rounded-full overflow-hidden">
                  <div className="h-full flex">
                    <div
                      className="h-full bg-primary-600 transition-all"
                      style={{ width: `${Math.min(otherMastersUsed, 100)}%` }}
                      title={`${otherMastersUsed}% other masters`}
                    />
                    <div
                      className={`h-full transition-all ${editWouldExceed ? 'bg-red-500' : 'bg-sky-500'}`}
                      style={{ width: `${Math.min(editAllocation, remainingForEdit)}%` }}
                      title={`${Math.min(editAllocation, remainingForEdit)}% this master`}
                    />
                  </div>
                </div>
                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-500 dark:text-gray-400">
                  <span>
                    <span className="inline-block w-2 h-2 rounded-sm bg-primary-600 mr-1 align-middle" />
                    Other masters {otherMastersUsed}%
                  </span>
                  <span>
                    <span className="inline-block w-2 h-2 rounded-sm bg-sky-500 mr-1 align-middle" />
                    This master {Math.min(editAllocation, remainingForEdit)}%
                  </span>
                  <span>
                    Still free {Math.max(0, remainingForEdit - Math.min(editAllocation, remainingForEdit))}%
                  </span>
                </div>
              </div>

              <div>
                <div className="flex flex-wrap items-center justify-between gap-1 mb-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-200">
                    Investment Amount (Allocation %)
                  </label>
                  {otherMastersUsed > 0 && (
                    <span className="text-xs text-gray-500">
                      Max {remainingForEdit}% left
                    </span>
                  )}
                </div>
                <div className="text-center mb-2">
                  <span className="text-3xl sm:text-4xl font-bold text-primary-600">{editAllocation}%</span>
                  <p className="text-xs text-gray-500 mt-1">
                    Copy {editAllocation}% of each trade from this master
                  </p>
                </div>
                <div className="relative w-full h-5 flex items-center">
                  <div className="absolute inset-x-0 h-3 rounded-full bg-gray-700 border border-gray-500/70 overflow-hidden">
                    <div
                      className="h-full bg-primary-500 rounded-full transition-all"
                      style={{
                        width: `${
                          editSliderMax > 0
                            ? (Math.min(editAllocation, editSliderMax) / editSliderMax) * 100
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                  <input
                    type="range"
                    min="0"
                    max={editSliderMax || 0}
                    value={Math.min(editAllocation, editSliderMax)}
                    onChange={(e) => setEditAllocation(Number(e.target.value))}
                    disabled={remainingForEdit <= 0}
                    className="allocation-slider absolute inset-0 w-full h-full z-10"
                  />
                </div>
                <div className="flex justify-between mt-1 text-xs text-gray-500">
                  <span>0%</span>
                  <span>{editSliderMax}%</span>
                </div>
                {editWouldExceed && (
                  <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
                    Other masters already use {otherMastersUsed}%. You can set at most {remainingForEdit}% here.
                  </p>
                )}
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:gap-3 mt-6">
              <button
                onClick={() => setShowEditModal(false)}
                className="flex-1 px-4 py-2 border-2 border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 font-semibold rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                disabled={updateMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                className="flex-1 px-4 py-2 bg-primary-600 dark:bg-primary-700 text-white font-semibold rounded-lg hover:bg-primary-700 dark:hover:bg-primary-800 disabled:opacity-50 flex items-center justify-center gap-2"
                disabled={updateMutation.isPending || editWouldExceed || remainingForEdit <= 0 || editAllocation < 1}
              >
                {updateMutation.isPending ? (
                  <>
                    <FiRefreshCw className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && shareRelationship && (
        <ShareCopyModal
          relationship={shareRelationship}
          onClose={() => {
            setShowShareModal(false);
            setShareRelationship(null);
          }}
        />
      )}
    </div>
  );
};

export default ActiveCopies;